import React from "react";
import type { GeneratorCode } from "@csk/core";
import {
  DemoWorkbenchCard,
  DemoWorkbenchCopy,
  DemoWorkbenchInset,
  DemoWorkbenchPill,
  DemoWorkbenchStack,
} from "@csk/demo-ui";
import type { Footprint } from "../types";

interface Constraint {
  op: "<=" | "<" | ">=" | ">" | "==";
  axis: GeneratorCode;
  value: number;
}
interface Region {
  region_id: string;
  V: string;
  constraints: Constraint[];
  notes?: string;
}

function satisfies(
  fp: Footprint,
  region: Region,
): { ok: boolean; failures: string[] } {
  const f: string[] = [];
  const c = fp.coordinates;
  for (const r of region.constraints) {
    const x = c[r.axis];
    const v = r.value;
    let ok = true;
    if (r.op === "<=") ok = x <= v;
    else if (r.op === "<") ok = x < v;
    else if (r.op === ">=") ok = x >= v;
    else if (r.op === ">") ok = x > v;
    else ok = x === v;
    if (!ok) f.push(`${r.axis} ${r.op} ${v} violated (got ${x})`);
  }
  return { ok: f.length === 0, failures: f };
}

export function RegimeRegionPanel(props: {
  footprint: Footprint | null;
  regions: Region[];
}): React.JSX.Element {
  const footprint = props.footprint;
  return (
    <DemoWorkbenchCard
      title="Regime regions (count-space demo)"
      right={<DemoWorkbenchPill>downset-ish</DemoWorkbenchPill>}
    >
      {!footprint ? (
        <DemoWorkbenchCopy>
          No footprint loaded. Generate and sync{" "}
          <code>BRIDGE_FOOTPRINT.json</code> into UI data.
        </DemoWorkbenchCopy>
      ) : (
        <DemoWorkbenchStack>
          {props.regions.map((r) => {
            const s = satisfies(footprint, r);
            return (
              <DemoWorkbenchInset key={r.region_id} compact>
                <div className="flex flex-wrap items-center gap-2">
                  <div className="font-semibold text-slate-100">
                    {r.region_id}
                  </div>
                  <DemoWorkbenchPill>
                    {s.ok ? "inside" : "outside"}
                  </DemoWorkbenchPill>
                  <DemoWorkbenchPill>V={r.V}</DemoWorkbenchPill>
                </div>
                {r.notes ? (
                  <DemoWorkbenchCopy className="mt-1">{r.notes}</DemoWorkbenchCopy>
                ) : null}
                <DemoWorkbenchCopy size="xs" tone="muted" className="mt-2">
                  Constraints:{" "}
                  {r.constraints
                    .map((c) => `${c.axis}${c.op}${c.value}`)
                    .join(", ")}
                </DemoWorkbenchCopy>
                {!s.ok ? (
                  <DemoWorkbenchCopy size="xs" tone="danger" className="mt-2">
                    {s.failures.map((x, i) => (
                      <div key={i}>- {x}</div>
                    ))}
                  </DemoWorkbenchCopy>
                ) : null}
              </DemoWorkbenchInset>
            );
          })}
        </DemoWorkbenchStack>
      )}
    </DemoWorkbenchCard>
  );
}
